import { fetchJSON as fetchAPI } from './client';

export type OperationsDashboardSnapshot = {
  tenant_id: string; active_tasks: number; pending_approvals: number; running_workflows: number;
  completion_rate_pct: number; automation_rate_pct: number; avg_resolution_hours: number;
  time_saved_hours: number; business_impact_usd: string; generated_at: string;
};

export type OperationalTask = {
  id: string; type: string; title: string; status: string; priority: string;
  assignee?: string; recommendation_id?: string; workflow_id?: string;
  due_at?: string; created_at: string;
};

export type WorkflowInstance = {
  id: string; name: string; template: string; status: string;
  steps_total: number; steps_done: number; task_ids?: string[]; started_at: string;
};

export type ApprovalRequest = {
  id: string; task_id: string; title: string; approval_type: string; status: string;
  requested_by: string; required_role?: string; amount_usd?: string; requested_at: string;
};

export type TaskAssignment = {
  task_id: string; assignee: string; assign_type: string; assigned_at: string;
};

export type ReminderEntry = {
  id: string; task_id: string; type: string; message: string; scheduled: string; status: string;
};

export type EscalationEntry = {
  id: string; task_id: string; reason: string; severity: string; status: string; escalated_at: string;
};

export type AutomationRule = {
  id: string; name: string; trigger: string; condition: string; action: string; enabled: boolean;
};

export type WorkflowAnalyticsSnapshot = {
  completion_rate_pct: number; avg_resolution_hours: number; automation_rate_pct: number;
  avg_approval_hours: number; execution_success_pct: number; business_impact_usd: string; time_saved_hours: number;
};

export type ExecutionHistoryEntry = {
  id: string; entity_type: string; entity_id: string; action: string; actor: string;
  before?: string; after?: string; outcome?: string; business_impact?: string;
  duration_ms: number; timestamp: string;
};

const COMMAND_BASE = import.meta.env.VITE_COMMANDCENTER_API ?? '/api/v1/command';
const fetchC = <T,>(path: string, init?: RequestInit) => fetchAPI<T>(`${COMMAND_BASE}${path}`, init);

export const autonomousOpsApi = {
  dashboard: () => fetchC<OperationsDashboardSnapshot>('/autonomous-ops/dashboard'),
  tasks: () => fetchC<{ tasks: OperationalTask[] }>('/autonomous-ops/tasks').then((r) => r.tasks),
  workflows: () => fetchC<{ workflows: WorkflowInstance[] }>('/autonomous-ops/workflows').then((r) => r.workflows),
  approvals: () => fetchC<{ approvals: ApprovalRequest[] }>('/autonomous-ops/approvals').then((r) => r.approvals),
  assignments: () => fetchC<{ assignments: TaskAssignment[] }>('/autonomous-ops/assignments').then((r) => r.assignments),
  reminders: () => fetchC<{ reminders: ReminderEntry[] }>('/autonomous-ops/reminders').then((r) => r.reminders),
  escalations: () => fetchC<{ escalations: EscalationEntry[] }>('/autonomous-ops/escalations').then((r) => r.escalations),
  automation: () => fetchC<{ rules: AutomationRule[] }>('/autonomous-ops/automation').then((r) => r.rules),
  analytics: () => fetchC<WorkflowAnalyticsSnapshot>('/autonomous-ops/analytics'),
  history: () => fetchC<{ events: ExecutionHistoryEntry[] }>('/autonomous-ops/history').then((r) => r.events),
  search: (query: string) =>
    fetchC<{ results: { id: string; type: string; title: string; summary?: string }[] }>(
      '/autonomous-ops/search',
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query }) },
    ).then((r) => r.results),
};
